// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * The owner-facing explanation of why an estimate moved, as a draft.
 *
 * Deliberately not a one-click export. The explanations are seeded from the
 * comparison notes the team wrote for themselves, and notes written for your
 * own side are not automatically fit to send: "we were light on this" is a
 * true and useful internal sentence and a terrible one to put in front of the
 * person paying. So every line is editable here, the wording is reviewed, and
 * only then does it get copied out.
 *
 * Edits stay in the draft. They do not write back over the internal note,
 * because the client-safe phrasing replacing the candid record would lose the
 * thing the record was for.
 */

import React, { useMemo, useState } from "react";
import type { Theme } from "../lib/theme";
import {
  narrativeSummary, lineSentence, omittedSentence,
  type ChangeNarrative, type NarrativeLine,
} from "../lib/changeNarrative";
import type { ShowToast } from "../types";

const money = (n: number) => {
  const s = "$" + Math.abs(Math.round(n)).toLocaleString();
  return n < 0 ? `(${s})` : s;
};

export function narrativeText(
  n: ChangeNarrative,
  lines: NarrativeLine[],
  projectName: string,
  fromLabel: string,
  toLabel: string,
): string {
  const out: string[] = [];
  out.push(projectName || "Project");
  out.push(`Estimate Summary — ${fromLabel} to ${toLabel}`);
  out.push("");
  out.push(narrativeSummary(n, fromLabel, toLabel));
  if (lines.length) {
    out.push("");
    out.push("What changed:");
    for (const l of lines) out.push(`  • ${lineSentence(l)}`);
  }
  const tail = omittedSentence(n);
  if (tail) {
    out.push("");
    out.push(tail);
  }
  return out.join("\n");
}

export default function ChangeNarrativeModal({
  T, FONT, projectName, fromLabel, toLabel, narrative, onClose, showToast,
}: {
  T: Theme;
  FONT: string;
  projectName: string;
  fromLabel: string;
  toLabel: string;
  narrative: ChangeNarrative;
  onClose: () => void;
  showToast: ShowToast;
}) {
  // Keyed by cost code so a line keeps its edit as the draft re-renders.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [dropped, setDropped] = useState<Set<string>>(new Set());

  const lines = useMemo(
    () => narrative.lines
      .filter((l) => !dropped.has(l.code))
      .map((l) => ({ ...l, note: edits[l.code] ?? l.note })),
    [narrative.lines, edits, dropped],
  );

  const stillMissing = lines.filter((l) => !l.note.trim()).length;
  const text = narrativeText(narrative, lines, projectName, fromLabel, toLabel);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      showToast("Summary copied — paste it into your email or letter");
    } catch {
      showToast("Could not copy. Select the text and copy manually.");
    }
  };

  const label: React.CSSProperties = {
    fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase",
    color: T.textWeak, fontWeight: 700,
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 300, background: T.bgModalBackdrop,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: T.bgModal, border: `1px solid ${T.borderSecondary}`, borderRadius: 10,
          width: "min(820px, 100%)", maxHeight: "90vh", display: "flex", flexDirection: "column",
          fontFamily: FONT, overflow: "hidden" }}
      >
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.borderPrimary}` }}>
          <div style={label}>Owner Summary — Draft</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, marginTop: 4 }}>
            {fromLabel} → {toLabel}
          </div>
        </div>

        {/* The one thing that must not be missed. */}
        <div style={{ padding: "10px 20px", background: `${T.borderAccent}14`,
          borderBottom: `1px solid ${T.borderAccent}44`, fontSize: 11, color: T.textSecondary, lineHeight: 1.6 }}>
          Explanations below are copied from your internal comparison notes. Read every one before
          this leaves the building — a note written for your own team is not automatically fit to
          send to an owner. Editing here changes the draft only; your internal notes stay as they are.
        </div>

        <div style={{ overflowY: "auto", padding: "18px 20px", flex: 1 }}>
          {/* Headline */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 14 }}>
            {[
              { k: fromLabel, v: money(narrative.fromTotal) },
              { k: toLabel, v: money(narrative.toTotal) },
              { k: "Change", v: `${narrative.delta > 0 ? "+" : ""}${money(narrative.delta)}`,
                color: narrative.delta > 0 ? T.negative : narrative.delta < 0 ? T.positive : T.textMuted },
            ].map((s) => (
              <div key={s.k}>
                <div style={label}>{s.k}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: s.color || T.textPrimary,
                  fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
              </div>
            ))}
          </div>

          <p style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.65, margin: "0 0 18px" }}>
            {narrativeSummary(narrative, fromLabel, toLabel)}
          </p>

          {stillMissing > 0 && (
            <div style={{ fontSize: 11, color: "#f6ad55", background: "#f6ad5514",
              border: "1px solid #f6ad5544", borderRadius: 6, padding: "8px 12px", marginBottom: 14 }}>
              {stillMissing} {stillMissing === 1 ? "change has" : "changes have"} no explanation.
              An owner reading a number with no reason beside it will ask for one.
            </div>
          )}

          {lines.length === 0 ? (
            <div style={{ fontSize: 12, color: T.textMuted, padding: "20px 0" }}>
              Nothing material changed between these two versions.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {lines.map((l) => (
                <div key={l.code} style={{ border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: "10px 12px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, color: T.textWeak, fontVariantNumeric: "tabular-nums" }}>{l.code}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary, flex: 1 }}>{l.scope}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                      color: l.delta > 0 ? T.negative : T.positive }}>
                      {l.delta > 0 ? "+" : ""}{money(l.delta)}
                    </span>
                    <button
                      onClick={() => setDropped((p) => new Set(p).add(l.code))}
                      title="Leave this change out of the summary"
                      style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer",
                        fontSize: 14, lineHeight: 1, padding: "0 2px", fontFamily: FONT }}
                    >
                      ×
                    </button>
                  </div>
                  <textarea
                    value={l.note}
                    onChange={(e) => setEdits((p) => ({ ...p, [l.code]: e.target.value }))}
                    placeholder="Why did this change? This sentence goes to the owner."
                    rows={2}
                    style={{ width: "100%", resize: "vertical", background: T.bgInput, color: T.textPrimary,
                      border: `1px solid ${l.note.trim() ? T.borderSecondary : "#f6ad5566"}`, borderRadius: 3,
                      padding: "6px 8px", fontSize: 12, fontFamily: FONT, lineHeight: 1.5, outline: "none" }}
                  />
                </div>
              ))}
            </div>
          )}

          {omittedSentence(narrative) && (
            <p style={{ fontSize: 12, color: T.textMuted, marginTop: 14, fontStyle: "italic" }}>
              {omittedSentence(narrative)}
            </p>
          )}

          {dropped.size > 0 && (
            <button
              onClick={() => setDropped(new Set())}
              style={{ marginTop: 12, background: "none", border: `1px solid ${T.borderSecondary}`,
                color: T.textMuted, borderRadius: 3, padding: "4px 10px", fontSize: 11,
                cursor: "pointer", fontFamily: FONT }}
            >
              Restore {dropped.size} removed {dropped.size === 1 ? "change" : "changes"}
            </button>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.borderPrimary}`,
          display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: T.textWeak }}>
            {lines.length} {lines.length === 1 ? "change" : "changes"} in the summary
          </span>
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textMuted,
              borderRadius: 3, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontFamily: FONT }}
          >
            Close
          </button>
          <button
            onClick={copy}
            style={{ background: T.borderAccent, border: "none", color: T.textOnGold, fontWeight: 700,
              borderRadius: 3, padding: "7px 16px", fontSize: 12, cursor: "pointer", fontFamily: FONT }}
          >
            Copy summary
          </button>
        </div>
      </div>
    </div>
  );
}
